/**
 * 2026-10-25: Lønartkoder til Danløn og DataLøn - tjek og forslag i
 * Løn › Regler › Lønartkoder, så fejlen ses ved feltet (og ikke først, når
 * lønfilen hentes). Serveren tjekker det samme, før filen laves.
 *
 * Danløn: løntyper, der kan indlæses fra fil ("Indlæs fra fil", Danløn 2025).
 * E = antal (timer, dage, stk), B = beløb (kroner).
 */
const DANLON = {};
const add = (nums, cols, name) => nums.forEach((n, i) => { DANLON[n] = { cols, name: typeof name === "function" ? name(i) : name }; });
add([1, 2, 3, 4, 5], "EB", (i) => `Timeløn ${i + 1}`);
add([6, 7, 8, 9, 10], "EB", (i) => `Overtid ${i + 1}`);
add([11, 12, 13, 14, 15], "EB", (i) => `Periodisk tillæg ${i + 1}`);
add([20, 21, 22, 23, 24], "B", (i) => `Periodisk fradrag ${i + 1}`);
Object.entries({
  30: ["B", "Tantieme"], 31: ["B", "Bestyrelseshonorar"], 32: ["B", "Honorar, bidragspligtigt"], 33: ["B", "Honorar, bidragsfrit"],
  34: ["B", "Bidragsfri A-indkomst"], 35: ["B", "Jubilæumsgratiale"], 36: ["B", "Fratrædelsesgodtgørelse"], 37: ["EB", "Diæter"],
  38: ["B", "Diæter udland"], 39: ["E", "Kilometerpenge"], 40: ["B", "Refusion af udlæg (skattefri)"], 41: ["B", "Tillæg ej ferieberettiget"],
  42: ["EB", "Fri kost"], 43: ["EB", "Rejsegodtgørelse"], 50: ["EB", "Sygedagpenge"], 51: ["EB", "Sygeløn"], 52: ["B", "Sygeferiepenge"],
  53: ["E", "Sygedage"], 60: ["E", "Afholdte feriedage"], 61: ["B", "Nettoferiepenge udbetalt"], 62: ["B", "Ferietillæg"],
  63: ["EB", "SH forskud"], 64: ["B", "SH hensat"], 65: ["B", "Udbetal fritvalgsbeløb"], 70: ["EB", "Hensat til afspadsering"],
  71: ["EB", "Afholdt afspadsering"], 72: ["E", "Planlagt flextid"], 73: ["E", "Faktisk flextid"], 80: ["EB", "Omsorgsdage udbetalt"],
  81: ["EB", "Feriefridage udbetalt (betaler penge ud)"], 82: ["EB", "Ledighedsdage"], 90: ["E", "A-skat fradrag dage"], 91: ["EB", "ATP"],
  92: ["B", "A-skat"], 93: ["B", "Gagereduktion"], 94: ["B", "Ret A-skat af feriepenge"], 95: ["B", "Ret SH-opsparing"],
  96: ["B", "Ret SH-forskud"], 97: ["B", "Ret Fritvalg, opsparing"], 98: ["B", "Ret Fritvalg, brugt"], 99: ["E", "Arbejdsdage med ferieoptjening"],
}).forEach(([n, [cols, name]]) => { DANLON[n] = { cols, name }; });

// Brugertilføjede typer (T6+/O6+), som super admin har navngivet eller skjult.
const DANLON_TO = {};

/** Danløn-løntypen bag en kode: { code, cols, name } - eller null. */
export function danlonType(code) {
  const c = String(code || "").trim().toUpperCase();
  const m = /^([TO]?)(\d{1,3})$/.exec(c);
  if (!m) return null;
  const n = Number(m[2]);
  if (m[1] === "T" && n >= 1 && n <= 5) return DANLON[n] ? { code: String(n), ...DANLON[n] } : null;
  if (m[1]) {
    const key = `${m[1]}${n}`;
    if (key in DANLON_TO) return DANLON_TO[key] ? { code: key, ...DANLON_TO[key] } : null;
    return n >= 6 ? { code: key, cols: "EB", name: m[1] === "T" ? `Timeløn ${n} (brugertilføjet)` : `Overtid ${n} (brugertilføjet)` } : null;
  }
  return DANLON[n] ? { code: String(n), ...DANLON[n] } : null;
}

/** Valgmuligheder til forslagsfeltet - kun løntyper, der tager enheden. */
export function danlonOptions(unit) {
  const need = unit === "kr" ? "B" : "E";
  return [...Object.entries(DANLON), ...Object.entries(DANLON_TO).filter(([, v]) => v)]
    .filter(([, v]) => v.cols.includes(need)).map(([n, v]) => ({ value: n, hint: v.name }));
}

/** Forslag (Danløn) pr. lønart - aftal altid numrene med bogholderen.
 * 2026-10-25: Ingen forslag til "Overtid til afspadsering" (O6/O7 er overtids-
 * typer, der UDBETALER - Danløn har ingen type til tillæg, der afspadseres), og
 * feriefridage sendes ikke som standard (81 = Feriefridage UDBETALT betaler dagen
 * ud oveni frihedskontoen; Danløn har ingen type til en afholdt feriefridag). */
export const DANLON_SUGGEST = {
  base: { timer: "1", overtimer: "2", ot1: "6", ot2: "7", aften: "11", nat: "12", afsp_timer: "70", ot1_afsp: "", ot2_afsp: "" },
  days: { syg: "53", barn_syg: "-", ferie: "60", feriefridag: "-", ferie_uden_loen: "-", fri_uden_loen: "-", afspadsering: "-" },
  hours: { syg: "51", barn_syg: "51", afspadsering: "71", ferie_uden_loen: "-", fri_uden_loen: "-" },
  unpaid: "-",
  kr: "41",
};

/**
 * DataLøn: løndelene fra DataLøns Lønbehandlingsoversigt (marts 2024). En
 * løndel er enten ét felt ("47" = Afholdt ferie, antal) eller et par antal x sats
 * ("12x13" = Normaltimer 1 x sats), som i filen skrives "1213". cat bruges til
 * advarsler: en lønart med løn må ikke gå til et fradrag, et personalegode osv.
 */
const DL = {};
const dl = (codes, name, cat = "") => codes.split(" ").forEach((c) => { DL[c] = { name, cat }; });
[["12x13", 1], ["14x15", 2], ["60x61", 3], ["62x63", 4], ["8Gx9G", 5], ["8Hx9H", 6]].forEach(([c, n]) => dl(c, `Normaltimer ${n} x sats`));
[["16x17", 1], ["18x19", 2], ["64x65", 3], ["66x67", 4], ["1Cx2C", 5], ["3Cx4C", 6], ["4Ix5I", 7], ["6Ix7I", 8]].forEach(([c, n]) => dl(c, `Overtid ${n} x sats`));
[["68x69", 1], ["70x71", 2], ["1Bx2B", 3], ["3Bx4B", 4], ["5Bx6B", 5], ["7Bx8B", 6]].forEach(([c, n]) => dl(c, `Genetillæg ${n} x sats`));
dl("01", "Arbejdstimer (kun antal)", "count"); dl("02 83 84 85 4J 1I", "Ferieberettiget løn"); dl("8F 6F 2I", "Ferieberettiget løn uden pension");
dl("03 1A 3A", "Ferieberettiget tillæg"); dl("26 3I", "Provision"); dl("20x21", "Akkord enheder x sats"); dl("22x23", "Tillæg x sats");
dl("2Gx3G 6Ex7E", "Tillæg uden pension, timer x sats"); dl("40", "Værdi af fri bil", "benefit"); dl("41", "Fri bil, egenbetaling", "deduction"); dl("43", "Fri telefon, egenbetaling", "deduction");
dl("42 5G 6G 7G", "Fri telefon", "benefit"); dl("44 1G", "Fri kost og logi", "benefit"); dl("1H 7H 2H 3H 4H 5H 6H 7L 8L", "Personalegode", "benefit");
dl("47", "Afholdt ferie i perioden, antal", "count"); dl("1U", "Udbetalte feriedage", "count"); dl("45", "Feriefridage/timer, opsparede", "count");
dl("77x78", "Feriefridage/feriefridagstimer x sats"); dl("79", "Feriefridagsbetaling"); dl("75x76", "Omsorgsdage/omsorgstimer x sats");
dl("4A", "Kompensation for fridage"); dl("90", "Afholdte feriedage uden løn", "count"); dl("8Ex9E", "Andre fraværsdage x sats med pension");
dl("96x97", "Andre fraværsdage/timer x sats"); dl("5Cx6C", "Barns 1. sygedag x sats"); dl("7C", "Fraværsdage med løn", "count");
dl("55 56", "Udbetalt ferietillæg"); dl("1X 2X", "Store bededagstillæg"); dl("04", "SH-betaling"); dl("8Ax9A", "SH-betaling x sats");
dl("1F 9F", "Fritvalgsbetaling"); dl("2Fx3F", "Udbetaling fritvalg timer x sats"); dl("4Fx5F", "Fritvalgsopsparing timer x sats", "count");
dl("7F", "Reguleringsbidrag, fritvalgsopsparing"); dl("1L", "Uregelmæssige betalinger"); dl("2Lx3L", "Uregelmæssige betalinger x sats");
dl("2A", "Efterregulering"); dl("4Lx5L", "Efterregulering x sats"); dl("6L", "Kompensationsbetaling uden AM-bidrag");
dl("74", "Dagpengetimer", "count"); dl("29 38", "Dagpenge uden AM-bidrag"); dl("4S", "Opskrivning af sygeferiepengegrundlag");
dl("05 5A 6A 7A 8I 9I", "Tillæg før skat"); dl("28 8C", "Ikke ferieberettiget løn"); dl("4Ex5E", "Ej ferieberettiget løn timer x sats");
dl("39 1M 2M 3M", "Bestyrelseshonorar"); dl("1N 2N", "Feriepenge før skat"); dl("98 1D 2D 3D 4D 5D 6D 7D 8D 9D", "Fradrag før skat", "deduction");
dl("1E 2E 3E", "Reduktion af bruttoløn", "deduction"); dl("30x31", "Skattepligtige km x sats"); dl("72", "Sygetimer (kun antal)", "count");
dl("1S", "Sygeløn uden pension med ferie", "sick"); dl("2S", "Sygeløn uden ferie og pension", "sick"); dl("3S", "Sygeløn uden ferie med pension", "sick");
dl("06", "Sygeløn med AM-bidrag 1", "sick"); dl("37", "Sygeløn med AM-bidrag 2", "sick"); dl("73", "Løn under sygdom, fast løn", "sick"); dl("34", "Ledighedsdage (G-dage)", "count");
dl("35", "Ledighedsdage, beløb i alt"); dl("07", "Skattefri godtgørelse", "taxfree"); dl("6Mx7M", "Skattefri godtgørelse x sats", "taxfree");
dl("24x25 32x33 8Mx9M", "Skattefri km", "taxfree"); dl("27 36", "B-indkomst"); dl("50 51 52 53", "Særlig udbetaling");
dl("1T", "Medarbejderaktier tildelt", "benefit"); dl("09", "Udbetalt a conto", "deduction"); dl("08 91 92 93 94", "Fradrag efter skat", "deduction");
dl("10 86 87 88 89", "Tillæg efter skat", "taxfree"); dl("11 1J", "Skattedage", "count"); dl("4G", "Sødage", "count"); dl("80", "Pensionsgrundlag", "count");
dl("81 82", "Reguleringsbidrag AMP", "deduction"); dl("48 49 57 58 59 95", "Løndel uden lønbehandling", "noproc"); dl("9B", "Afspadsering (står blandt løndele uden lønbehandling)", "noproc");

/**
 * 2026-10-25: Lønsystemernes lister er de samme for alle kunder og vedligeholdes
 * ét sted: de indbyggede her + super admins tilføjelser, navne og skjulte
 * (Super admin › Lønsystemer, GET /payroll/pay-systems). Kunden vælger selv sine
 * koder og sin løntermin.
 */
const clone = (o) => JSON.parse(JSON.stringify(o));
const BASE_DANLON = clone(DANLON);
const BASE_DL = clone(DL);
/** DataLøns lønterminer (Minuba/Lønguiden; DataLøns ordbog nævner de fire terminer). */
const BASE_TERMS = [["51", "Månedsløn, bagud"], ["41", "Månedsløn, forud"], ["24", "14-dages, ulige uger (torsdag)"],
  ["25", "14-dages, ulige uger (fredag)"], ["34", "14-dages, lige uger (torsdag)"], ["35", "14-dages, lige uger (fredag)"]].map(([code, label]) => ({ code, label }));
let TERMS = clone(BASE_TERMS);

export const danlonBase = () => BASE_DANLON;
export const dataloenBase = () => BASE_DL;
export const termsBase = () => BASE_TERMS;
export const payTerms = () => TERMS;

/** Læg super admins liste (fra serveren) oven på de indbyggede - og firmaets egne
 * Danløn-typer (T6+/O6+, som firmaet selv har oprettet i Danløn). */
export function applyPayCatalog(cat, own = []) {
  Object.keys(DANLON).forEach((k) => delete DANLON[k]);
  Object.assign(DANLON, clone(BASE_DANLON));
  Object.keys(DANLON_TO).forEach((k) => delete DANLON_TO[k]);
  (cat?.danlon || []).forEach((e) => {
    const m = /^([TO]?)(\d{1,3})$/.exec(String(e.code || "").toUpperCase());
    if (!m) return;
    if (m[1]) { DANLON_TO[`${m[1]}${Number(m[2])}`] = e.hidden ? null : { cols: e.cols || "EB", name: e.name || `${m[1]}${Number(m[2])}` }; return; }
    const n = Number(m[2]);
    if (e.hidden) delete DANLON[n];
    else DANLON[n] = { cols: e.cols || "EB", name: e.name || BASE_DANLON[n]?.name || `Løntype ${n}` };
  });
  (own || []).forEach((t) => {
    const m = /^([TO])0*(\d{1,2})$/.exec(String(t.code || "").toUpperCase());
    if (m && Number(m[2]) >= 6) DANLON_TO[`${m[1]}${Number(m[2])}`] = { cols: t.cols || "EB", name: t.name ? `${t.name} (egen)` : `${m[1] === "T" ? "Timeløn" : "Overtid"} ${Number(m[2])} (egen)` };
  });
  Object.keys(DL).forEach((k) => delete DL[k]);
  Object.assign(DL, clone(BASE_DL));
  (cat?.dataloen || []).forEach((e) => {
    if (e.hidden) delete DL[e.code];
    else DL[e.code] = { name: e.name || BASE_DL[e.code]?.name || e.code, cat: e.cat || "" };
  });
  const terms = clone(BASE_TERMS);
  (cat?.terms || []).forEach((e) => {
    const i = terms.findIndex((t) => t.code === e.code);
    if (e.hidden) { if (i >= 0) terms.splice(i, 1); return; }
    if (i >= 0) terms[i] = { code: e.code, label: e.label || terms[i].label };
    else terms.push({ code: e.code, label: e.label || e.code });
  });
  TERMS = terms;
}

/** DataLøn-løndelen bag en kode (som den sendes, 4 tegn): { sent, name, cat } - eller null (ukendt). */
export function dataloenType(code) {
  const sent = String(code || "").trim().toUpperCase().padStart(4, "0");
  if (!/^[0-9A-Z]{4}$/.test(sent)) return null;
  const pair = `${sent.slice(0, 2)}x${sent.slice(2)}`;
  if (DL[pair]) return { sent, pair: true, ...DL[pair] };
  if (sent.startsWith("00") && DL[sent.slice(2)]) return { sent, pair: false, ...DL[sent.slice(2)] };
  return null;
}
const DL_WARN = {
  deduction: "er et fradrag - lønarten giver løn, så den ville blive trukket fra lønnen",
  benefit: "er et personalegode - ikke løn",
  taxfree: "er skattefri - ikke almindelig løn",
  noproc: "er en løndel uden lønbehandling - giver ingen løn",
  sick: "er sygeløn - ikke almindelig løn",
  count: "registrerer kun antal - giver ingen løn",
};

/**
 * Tjek én kode. unit: "timer" | "dage" | "stk" | "kr". paid: linjen bærer løn
 * (så "-" betyder, at nogen ikke får løn for timerne).
 * Svar: { level: "error" | "warn" | "ok" | "", text, suggest }
 */
export function checkCode(system, value, unit, { paid = false, suggest = "", kind = "" } = {}) {
  const v = String(value || "").trim();
  if (v === "-") return paid ? { level: "warn", kind: "dash", text: "Sendes ikke - lønsystemet får ikke disse timer", suggest } : { level: "", text: "Sendes ikke" };
  if (!v) return system === "generic" ? { level: "", text: "" } : { level: "error", text: "Mangler kode", suggest };
  if (system === "danlon") {
    const t = danlonType(v);
    if (!t) return { level: "error", text: `"${v}" er ikke en Danløn-løntype (Danløn bruger numre, fx 1 = Timeløn 1)`, suggest };
    const need = unit === "kr" ? "B" : "E";
    if (!t.cols.includes(need)) {
      return { level: "error", text: `${t.code} ${t.name} tager kun ${t.cols === "B" ? "beløb" : "antal"} - ${unit === "kr" ? "kroner" : unit} kan ikke indlæses`, suggest };
    }
    return { level: "ok", text: t.name };
  }
  if (system === "dataloen") {
    if (!/^[0-9A-Za-z]{1,4}$/.test(v)) return { level: "error", text: "DataLøns løndele er 4 tegn, fx 1213 eller 009B", suggest };
    const sent = v.toUpperCase().padStart(4, "0");
    // DataLøns løndele er tal (evt. med et bogstav til sidst, fx 009B) - "OT1" ligner en forkortelse.
    if (/^[A-Za-z]/.test(v) && !dataloenType(v)) return { level: "warn", kind: "abbr", text: `Ligner en forkortelse - sendes som ${sent}. Brug løndelens nummer fra DataLøn` };
    // 2026-10-25 (L2): Løndelens navn fra DataLøns oversigt, og en advarsel, når betydningen ikke passer.
    const t = dataloenType(v);
    if (!t) return { level: "warn", kind: "unknown", text: `${sent} findes ikke i listen over DataLøns løndele (oversigten fra marts 2024 + super admins tilføjelser) - tjek nummeret med bogholderen` };
    const bad = t.cat && (
      (paid && kind !== "extra" && ["deduction", "benefit", "taxfree", "noproc", "count"].includes(t.cat) && !(kind === "afsp" && t.cat === "noproc"))
      || (paid && kind === "extra" && ["deduction", "benefit", "noproc"].includes(t.cat))
      || (paid && kind === "base" && t.cat === "sick")
      || (!paid && t.cat === "benefit"));
    if (bad) return { level: "warn", kind: "meaning", text: `${sent} = ${t.name} - ${DL_WARN[t.cat]}` };
    // Et enkelt felt (fx 0006) er et beløb - et par (fx 1213) er antal x sats, og "kun antal"-felter er antal.
    if (unit !== "kr" && !t.pair && t.cat !== "count" && t.cat !== "noproc") {
      return { level: "warn", kind: "meaning", text: `${sent} = ${t.name} - et beløbsfelt; ${unit} kan ikke sendes som antal. Brug en løndel, der tager antal (fx et par antal x sats)` };
    }
    if (unit === "kr" && (t.pair || t.cat === "count")) {
      return { level: "warn", kind: "meaning", text: `${sent} = ${t.name} - tager antal${t.pair ? " x sats" : ""}, ikke et beløb i kroner` };
    }
    return { level: "ok", text: `${sent} = ${t.name}` };
  }
  return { level: "", text: "" };
}
